//membershipOfferForm.js

import React, { useState, useEffect, useRef } from "react";
import Flatpickr from "flatpickr";
import "flatpickr/dist/flatpickr.css";
import { getMembershipPlanCount, getMembershipPlans } from "../../membership-plans/helper/api";
import { isAuthenticated } from "../../../auth";
import Toast from "../../../lib/toast"; 
import "../style.scss";

export const DesktopModalCloseButton = ({ onClose }) => (
  <div
    onClick={onClose}
    className="membership-offer-modal__close-btn"
    role="button"
    aria-label="Close"
  >
    <svg width="24" height="24" viewBox="0 0 19 19" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M4.9283 3.80866C4.77899 3.66445 4.57901 3.58465 4.37144 3.58646C4.16387 3.58826 3.96531 3.67152 3.81853 3.8183C3.67175 3.96508 3.58849 4.16364 3.58668 4.37121C3.58488 4.57879 3.66468 4.77876 3.80888 4.92807L8.38076 9.49995L3.80809 14.0718C3.73248 14.1449 3.67217 14.2322 3.63068 14.3288C3.58919 14.4254 3.56735 14.5293 3.56644 14.6344C3.56552 14.7395 3.58555 14.8437 3.62536 14.941C3.66516 15.0383 3.72395 15.1267 3.79828 15.2011C3.87261 15.2754 3.961 15.3342 4.0583 15.374C4.15559 15.4138 4.25983 15.4338 4.36495 15.4329C4.47007 15.432 4.57395 15.4101 4.67054 15.3687C4.76712 15.3272 4.85448 15.2669 4.92751 15.1912L9.50018 10.6194L14.0721 15.1912C14.2214 15.3354 14.4213 15.4152 14.6289 15.4134C14.8365 15.4116 15.035 15.3284 15.1818 15.1816C15.3286 15.0348 15.4119 14.8363 15.4137 14.6287C15.4155 14.4211 15.3357 14.2211 15.1915 14.0718L10.6196 9.49995L15.1915 4.92807C15.3357 4.77876 15.4155 4.57879 15.4137 4.37121C15.4119 4.16364 15.3286 3.96508 15.1818 3.8183C15.035 3.67152 14.8365 3.58826 14.6289 3.58646C14.4213 3.58465 14.2214 3.66445 14.0721 3.80866L9.50018 8.38053L4.9283 3.80786V3.80866Z" fill="black" />
    </svg>
  </div>
);


const FloatingField = ({
  fieldRef,
  modifier, // "" | "title" | "discount" | "start" | "end"
  label,
  active,
  onClick,
  children,
}) => (
  <div
    ref={fieldRef}
    onClick={onClick}
    className={`membership-offer-modal__field${modifier ? ` membership-offer-modal__field--${modifier}` : ""}${
      active ? " membership-offer-modal__field--active" : ""
    }`}
  >
    <span
      className={`membership-offer-modal__field-label${
        modifier ? ` membership-offer-modal__field-label--${modifier}` : ""
      }${active ? " membership-offer-modal__field-label--active" : ""}`}
    >
      {label}
    </span>
    {children}
  </div>
);


export const MembershipOfferForm = ({ onClose, onSave, initialData = null, hideHeader = false }) => {
  const isEditMode = Boolean(initialData);

  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [plansError, setPlansError] = useState(null);

  const [selectedPlanId, setSelectedPlanId] = useState(initialData?.planId || "");

  const [offerTitle, setOfferTitle] = useState(initialData?.offerTitle || "");
  const [isTitleFocused, setTitleFocused] = useState(false);

  const [isPercentageDiscount, setPercentageDiscount] = useState(
    initialData ? initialData.isPercentageDiscount : true
  );

  const [discountValue, setDiscountValue] = useState(initialData?.discountValue ?? "");
  const [isDiscountFocused, setDiscountFocused] = useState(false);
  const [startDateTime, setStartDateTime] = useState(initialData?.startDateTime || "");
  const [endDateTime, setEndDateTime] = useState(initialData?.endDateTime || "");

  const [isPlanDropdownOpen, setPlanDropdownOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const planFieldRef = useRef(null);

  const startInputRef = useRef(null);
  const endInputRef = useRef(null);
  const startPickerRef = useRef(null);
  const endPickerRef = useRef(null);
  const discountInputRef = useRef(null);
  const titleInputRef = useRef(null);

  useEffect(() => {
    let isMounted = true;

    const fetchPlans = async () => {
      setPlansLoading(true);
      setPlansError(null);
      try {
        const auth = isAuthenticated();
        if (!auth) {
          if (isMounted) {
            setPlansError("You must be signed in to load membership plans.");
            setPlansLoading(false);
          }
          return;
        }
        const { token, user } = auth;
        const userId = user._id;

        const branchId = localStorage.getItem("branchId");
        const baseQuery = { isPublished: true };
        if (branchId) baseQuery.branchId = branchId;

        const countRes = await getMembershipPlanCount(userId, token, baseQuery);
        const total =
          countRes?.count ??
          countRes?.data?.count ??
          (typeof countRes === "number" ? countRes : 0) ??
          0;

        const plansRes = await getMembershipPlans(userId, token, {
          ...baseQuery,
          limit: total || 100,
          page: 1,
        });

        if (plansRes?.error) {
          throw new Error(plansRes.error);
        }

        const fetchedPlans =
          plansRes?.membershipplans ??
          plansRes?.membershipPlans ??
          plansRes?.data ??
          plansRes?.plans ??
          (Array.isArray(plansRes) ? plansRes : []) ??
          [];

        if (!isMounted) return;

        const normalizedPlans = (Array.isArray(fetchedPlans) ? fetchedPlans : []).map(
          (plan) => {
            const pkg = (plan.packages && plan.packages[0]) || {};
            const durationLabel =
              pkg.duration != null && pkg.durationType
                ? `${pkg.duration} ${pkg.durationType}${pkg.duration > 1 ? "s" : ""}`
                : "";

            return {
              id: String(plan.id ?? plan._id),
              name: plan.name ?? "Unnamed Plan",
              duration: durationLabel,
              session: pkg.sessions ?? 0,
              amount: Number(pkg.basePrice ?? 0),
            };
          }
        );

        setPlans(normalizedPlans);
        if (normalizedPlans.length > 0) {
          if (isEditMode && normalizedPlans.some((p) => p.id === initialData.planId)) {
            setSelectedPlanId(initialData.planId);
          }
        }
      } catch (err) {
        if (!isMounted) return;
        console.error("Failed to fetch membership plans:", err);
        setPlansError("Failed to load membership plans. Please try again.");
      } finally {
        if (isMounted) setPlansLoading(false);
      }
    };

    fetchPlans();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (planFieldRef.current && !planFieldRef.current.contains(e.target)) {
        setPlanDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedPlan =
    plans.find((p) => p.id === selectedPlanId) || { id: "", name: "", duration: "", session: 0, amount: 0 };

  const discountAmount = isPercentageDiscount
    ? Math.round((selectedPlan.amount * (Number(discountValue) || 0)) / 100)
    : Number(discountValue) || 0;

  const handleToggleDiscountType = () => {
    setDiscountValue("");
    setPercentageDiscount(!isPercentageDiscount);
  };

  const handleCreate = async () => {
    const payload = {
      planId: selectedPlan.id,
      planName: selectedPlan.name,
      offerTitle,
      duration: selectedPlan.duration,
      session: selectedPlan.session,
      amount: selectedPlan.amount,
      isPercentageDiscount,
      discountValue,
      discountAmount,
      startDateTime,
      endDateTime,
    };

    setIsSaving(true);
    try {
      if (onSave) {
      
        await onSave(payload);
      } else {
        console.log(payload);
      }

      new Toast({
        type: "success",
        message: isEditMode ? "Membership Offer updated" : "Membership Offer created",
        duration: 3000,
      });

      if (!onSave) {
        onClose();
      }
    } catch (error) {
      new Toast({
        type: "error",
        message: isEditMode ? "Error updating the offer" : "Error adding the offer",
        duration: 3000,
      });
    } finally {
      setIsSaving(false);
    }
  };

 
  useEffect(() => {
    if (!startInputRef.current || !endInputRef.current) return;

    startPickerRef.current = Flatpickr(startInputRef.current, {
      enableTime: true,
      dateFormat: "d-m-Y h:i K",
      time_12hr: true,
      disableMobile: true,
      appendTo: document.body,
      defaultDate: initialData?.startDateTime || undefined,
      onChange: (selectedDates) =>
        setStartDateTime(selectedDates[0] ? selectedDates[0].toISOString() : ""),
    });

    endPickerRef.current = Flatpickr(endInputRef.current, {
      enableTime: true,
      dateFormat: "d-m-Y h:i K",
      time_12hr: true,
      disableMobile: true,
      appendTo: document.body,
      defaultDate: initialData?.endDateTime || undefined,
      onChange: (selectedDates) =>
        setEndDateTime(selectedDates[0] ? selectedDates[0].toISOString() : ""),
    });

    return () => {
      startPickerRef.current?.destroy();
      endPickerRef.current?.destroy();
      startPickerRef.current = null;
      endPickerRef.current = null;
    };
  }, []);

  const isPlanActive = isPlanDropdownOpen || Boolean(selectedPlanId);
  const isTitleActive = isTitleFocused || Boolean(offerTitle);
  const isDiscountActive = isDiscountFocused || Boolean(discountValue);
  const isStartActive = Boolean(startDateTime);
  const isEndActive = Boolean(endDateTime);
  const isSubmitDisabled = plansLoading || plans.length === 0 || !selectedPlanId || isSaving;

  const summaryRows = [
    ["Name", selectedPlanId ? selectedPlan.name : "–"],
    ["Duration", selectedPlanId ? selectedPlan.duration || "–" : "–"],
    ["Session", selectedPlanId ? selectedPlan.session : "–"],
    ["Amount", selectedPlanId ? `₹ ${selectedPlan.amount}` : "–"],
    ["Discount", selectedPlanId ? `₹ ${discountAmount}` : "–"],
  ];

  return (
    <div className="membership-offer-modal">
      {!hideHeader && (
        <div className="membership-offer-modal__header">
          <div className="membership-offer-modal__header-left">
            <svg
              className="membership-offer-modal__header-icon"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M3 4H21V16H3V4Z" stroke="#ffffff" strokeWidth="1.5" strokeLinejoin="round" />
              <path d="M8 20H16" stroke="#ffffff" strokeWidth="1.5" strokeLinecap="round" />
              <path d="M12 16V20" stroke="#ffffff" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
            <p className="membership-offer-modal__title">
              {isEditMode ? "Edit Membership Offer" : "Membership Offer"}
            </p>
          </div>
          <DesktopModalCloseButton onClose={onClose} />
        </div>
      )}

      {/* Body */}
      <div className="membership-offer-modal__body">
        <p className="membership-offer-modal__note">
          Note: Select a membership plan and add discount amount on it.
        </p>

        {/* Plan select */}
        <div className="membership-offer-modal__plan-field-wrap">
          {plansLoading ? (
            <p className="membership-offer-modal__field-status">Loading plans…</p>
          ) : plansError ? (
            <p className="membership-offer-modal__field-status membership-offer-modal__field-status--error">
              {plansError}
            </p>
          ) : plans.length === 0 ? (
            <p className="membership-offer-modal__field-status">No membership plans found.</p>
          ) : (
            <FloatingField
              fieldRef={planFieldRef}
              modifier=""
              label="Select Membership Plan *"
              active={isPlanActive}
              onClick={() => setPlanDropdownOpen(!isPlanDropdownOpen)}
            >
              <input
                readOnly
                value={selectedPlan.name || ""}
                type="text"
                placeholder={isPlanActive ? "Select Membership Plan" : undefined}
                className="membership-offer-modal__field-input membership-offer-modal__field-input--plan"
              />
              <div
                className={`membership-offer-modal__dropdown-arrow${
                  isPlanDropdownOpen ? " membership-offer-modal__dropdown-arrow--open" : ""
                }`}
              >
                <svg width="13" height="8" viewBox="0 0 13 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M1 1L6.5 6.5L12.5 1" stroke="#055075" strokeLinecap="round" />
                </svg>
              </div>

              {isPlanDropdownOpen && (
                <div className="membership-offer-modal__dropdown-menu">
                  {plans.map((plan) => (
                    <div
                      key={plan.id}
                      onClick={() => {
                        setSelectedPlanId(plan.id);
                        setPlanDropdownOpen(false);
                      }}
                      className="membership-offer-modal__dropdown-item"
                    >
                      <p>{plan.name}</p>
                    </div>
                  ))}
                </div>
              )}
            </FloatingField>
          )}
        </div>

        {/* Offer title */}
        <FloatingField
          modifier="title"
          label="Offer Title"
          active={isTitleActive}
          onClick={() => titleInputRef.current && titleInputRef.current.focus()}
        >
          <input
            ref={titleInputRef}
            type="text"
            value={offerTitle}
            onChange={(e) => setOfferTitle(e.target.value)}
            onFocus={() => setTitleFocused(true)}
            onBlur={() => setTitleFocused(false)}
            placeholder={isTitleActive ? "Ex. Summer Offer" : undefined}
            className="membership-offer-modal__field-input"
          />
        </FloatingField>

        {/* Toggle */}
        <div className="membership-offer-modal__toggle-row">
          <p className="membership-offer-modal__toggle-label">
            {isPercentageDiscount ? "Discount in Percentage (%)" : "Discount in Rupees (₹)"}
          </p>
          <div onClick={handleToggleDiscountType} className="membership-offer-modal__toggle">
            <div
              className={`membership-offer-modal__toggle-knob${
                isPercentageDiscount ? " membership-offer-modal__toggle-knob--on" : ""
              }`}
            />
          </div>
        </div>

        {/* Discount value */}
        <FloatingField
          modifier="discount"
          label={`Select discount in ${isPercentageDiscount ? "percentage" : "rupees"}`}
          active={isDiscountActive}
          onClick={() => discountInputRef.current && discountInputRef.current.focus()}
        >
          <input
            ref={discountInputRef}
            type="number"
            value={discountValue}
            onChange={(e) => setDiscountValue(e.target.value)}
            onFocus={() => setDiscountFocused(true)}
            onBlur={() => setDiscountFocused(false)}
            placeholder={isDiscountActive ? (isPercentageDiscount ? "e.g. 20" : "e.g. 500") : undefined}
            className="membership-offer-modal__field-input"
          />
        </FloatingField>

        {/* Start/End date time */}
        <div className="membership-offer-modal__date-row">
          <FloatingField
            modifier="start"
            label="Start Date & Time"
            active={isStartActive}
            onClick={() => startPickerRef.current?.open()}
          >
            <input
              ref={startInputRef}
              type="text"
              readOnly
              placeholder={isStartActive ? "DD-MM-YYYY  hh:mm AM/PM" : undefined}
              className="membership-offer-modal__field-input"
            />
          </FloatingField>
          <FloatingField
            modifier="end"
            label="End Date & Time"
            active={isEndActive}
            onClick={() => endPickerRef.current?.open()}
          >
            <input
              ref={endInputRef}
              type="text"
              readOnly
              placeholder={isEndActive ? "DD-MM-YYYY  hh:mm AM/PM" : undefined}
              className="membership-offer-modal__field-input"
            />
          </FloatingField>
        </div>

        <div className="membership-offer-modal__separator" />

        {/* Summary */}
        <p className="membership-offer-modal__summary-title">Plan Offer Summery</p>
        <div className="membership-offer-modal__summary-box">
          {summaryRows.map(([label, value]) => (
            <div key={label} className="membership-offer-modal__summary-row">
              <p className="membership-offer-modal__summary-label">{label}</p>
              <p className="membership-offer-modal__summary-value">{value}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Footer */}
      <div className="membership-offer-modal__footer">
        <div onClick={onClose} className="membership-offer-modal__btn membership-offer-modal__btn--cancel">
          <p>Cancel</p>
        </div>
        <div
          onClick={handleCreate}
          className={`membership-offer-modal__btn membership-offer-modal__btn--confirm${
            isSubmitDisabled ? " membership-offer-modal__btn--disabled" : ""
          }`}
        >
          <p>{isSaving ? "Saving..." : isEditMode ? "Update" : "Create"}</p>
        </div>
      </div>
    </div>
  );
};

export default MembershipOfferForm;